<template>
    <div class="color-picker" @mouseup="handleMouseUp" @mousemove="handleBaseColor" ref="color_picker">
        <div class="color-picker-grid"
             @mousemove="handleGrid"
             @mousedown="mouse_down_grid = true"
             ref="picker_grid"
        >
            <div class="color-picker-v" :style="`background-image: linear-gradient(to right, #ffffff, #${base_color})`"/>
            <div class="color-picker-s"/>
            <div class="color-picker-circle" :style="{top: y_grid_coord - 5 + 'px', left: x_grid_coord - 5 + 'px', backgroundColor: '#' + local_value}"></div>
        </div>
        <div class="color-picker-body">
            <div class="color-picker-h"

                 @mousedown="mouse_down_base = true"
                 ref="picker_base"
            />
            <div class="color-picker-circle-base" :style="{left: x_base_coord + 'px'}"></div>
            <div class="d-flex align-items-center mt-2">
                <div class="color-picker-preview mr-2" :style="`background-color: #${local_value}`"/>
                <span class="color-picker-hashtag">#</span>
                <input type="text" class="color-picker-input" v-model.lazy="local_value"/>
            </div>
        </div>
    </div>
</template>

<script>
    export default  {
        props: {
            value: {type: String, default: '#7BDE0E'},
        },

        data() {
            return {
                mouse_down_grid: false,
                mouse_down_base: false,
                x_grid_coord: 0,
                y_grid_coord: 0,
                x_base_coord: 0,
            };
        },

        computed: {
            local_value: {
                get() {
                    return this.value.replace('#', '');
                },
                set(value) {
                    this.$emit('input', `#${value}`);
                },

            },
            rgb() {
                return this.hexToRgb(this.local_value);
            },

            hsv() {
                return this.rgbToHsv(this.rgb);
            },

            base_color() {
                return this.rgbToHex(this.hsvToRgb({
                    h: this.hsv.h,
                    s: 1,
                    v: 1,
                }));
            },
        },

        methods: {
            hexToRgb(hex) {
                let split = hex.split('');
                return {
                    r: parseInt(split.splice(0, 2).join(''), 16),
                    g: parseInt(split.splice(0, 2).join(''), 16),
                    b: parseInt(split.splice(0, 2).join(''), 16),
                };
            },

            rgbToHex({r, g, b}) {
                r = r.toString(16).padStart(2, "0");
                g = g.toString(16).padStart(2, "0");
                b = b.toString(16).padStart(2, "0");
                return `${r}${g}${b}`;
            },

            rgbToHsv({r, g, b}) {
                r /= 255;
                g /= 255;
                b /= 255;

                let min = Math.min(r, g, b);
                let max = Math.max(r, g, b);
                let h, s, v = max;

                var d = max - min;
                s = max === 0 ? 0 : d / max;

                if (max === min) {
                    h = 0; // achromatic
                } else {
                    switch (max) {
                        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                        case g: h = (b - r) / d + 2; break;
                        case b: h = (r - g) / d + 4; break;
                    }
                    h /= 6;
                }

                return {h, s, v};
            },

            hsvToRgb({h, s, v}) {
                let r, g, b;

                let i = Math.floor(h * 6);
                let f = h * 6 - i;
                let p = v * (1 - s);
                let q = v * (1 - f * s);
                let t = v * (1 - (1 - f) * s);

                switch (i % 6) {
                    case 0:
                        r = v;
                        g = t;
                        b = p;
                        break;
                    case 1:
                        r = q;
                        g = v;
                        b = p;
                        break;
                    case 2:
                        r = p;
                        g = v;
                        b = t;
                        break;
                    case 3:
                        r = p;
                        g = q;
                        b = v;
                        break;
                    case 4:
                        r = t;
                        g = p;
                        b = v;
                        break;
                    case 5:
                        r = v;
                        g = p;
                        b = q;
                        break;
                }

                return {
                    r: Math.round(r * 255),
                    g: Math.round(g * 255),
                    b: Math.round(b * 255),
                };
            },

            handleBaseColor(ev) {
                if (!this.mouse_down_base) {
                    return;
                }
                let offsetX = ev.offsetX;
                let element = ev.target;

                while (this.$refs.color_picker !== element) {
                    offsetX += element.offsetLeft;
                    element = element.parentNode;
                }

                let colorbar_width = this.$refs.picker_base.getBoundingClientRect().width;
                let x_position = Math.max(0, Math.min(colorbar_width - 1, offsetX));

                this.local_value = this.rgbToHex(this.hsvToRgb({
                    h: 1 / colorbar_width * x_position,
                    s: this.hsv.s,
                    v: this.hsv.v,
                }));
                this.x_base_coord = colorbar_width * this.hsv.h;
            },

            handleGrid(ev) {
                if (!this.mouse_down_grid) {
                    return;
                }
                this.x_grid_coord = ev.offsetX;
                this.y_grid_coord = ev.offsetY;
                let rect = this.$refs.picker_grid.getBoundingClientRect();
                let {h} = this.hsv;
                let s = Math.max(0, 1 / rect.width * ev.offsetX);
                let v = Math.min(1, 1 - (1 / rect.height * ev.offsetY));

                this.local_value = this.rgbToHex(this.hsvToRgb({h, s, v}));
            },

            handleMouseUp(ev) {
                if (this.mouse_down_grid) {
                    this.handleGrid(ev);
                } else {
                    this.handleBaseColor(ev);
                }
                this.mouse_down_grid = false;
                this.mouse_down_base = false;
            },

            updateIndicatorPosition() {
                // set coordinates on mount to draw initial location circles
                let base_rect = this.$refs.picker_base.getBoundingClientRect();
                let grid_rect = this.$refs.picker_grid.getBoundingClientRect();
                let {h, s, v} = this.hsv;
                this.x_base_coord = h * base_rect.width;
                this.x_grid_coord = s * grid_rect.width;
                this.y_grid_coord = (1-v) * grid_rect.height;
            },
        },

        watch: {
            local_value() {
                // roll back to default value if something goes wrong in color calculation
                if (Number.isNaN(this.hsv.s)) {
                    this.local_value = '7BDE0E';
                }
                this.updateIndicatorPosition();
            },
        },

        mounted() {
            this.updateIndicatorPosition();
        },
    };
</script>